import { AddressInfo } from "@redotech/redo-customer-portal-shared/hooks/useAddress/use-address";
import { checkBlockingAddressErrors } from "@redotech/redo-customer-portal-shared/utils/address-validation";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import { Address as RedoAddress } from "@redotech/redo-model/draft-return/draft-return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { filterTruthy } from "@redotech/util/array";
import { memo, useMemo } from "react";
import { useVerifyAddress } from "../../../hooks/useVerifyAddress";

export function Address({
  addressInfo,
  continueDisabled = false,
  showBorder = true,
}: {
  addressInfo: AddressInfo;
  continueDisabled?: boolean;
  showBorder?: boolean;
}) {
  const verifyAddressQuery = useVerifyAddress({ address: addressInfo.address });

  const blockingError = useMemo(() => {
    return checkBlockingAddressErrors(
      addressInfo.address?.street1 ?? undefined,
      addressInfo.address?.street2 ?? undefined,
      addressInfo.address?.country ?? undefined,
      addressInfo.type,
    );
  }, [
    addressInfo.address?.street1,
    addressInfo.address?.street2,
    addressInfo.address?.country,
    addressInfo.type,
  ]);

  const showAddressVerificationError =
    (verifyAddressQuery.isSuccess &&
      !verifyAddressQuery.data?.delivery?.success) ||
    verifyAddressQuery.isError;

  const hasAddressError = blockingError || showAddressVerificationError;

  return (
    <Flex
      borderColor={showBorder ? "secondary" : "transparent"}
      borderStyle={showBorder ? "solid" : "none"}
      borderWidth="1px"
      dir="column"
      px={showBorder ? "2xl" : "none"}
      py="xl"
      radius="md"
    >
      <Flex align="flex-start" justify="space-between">
        <Flex flexDirection="column" gap="lg">
          <Flex>
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {addressInfo.title}
            </Text>
          </Flex>
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="thin" textColor="secondary">
              {addressInfo.addressText1}
            </Text>
            {!!addressInfo.addressText2 && (
              <Text fontSize="sm" fontWeight="thin" textColor="secondary">
                {addressInfo.addressText2}
              </Text>
            )}
            {!!addressInfo.addressText3 && (
              <Text fontSize="sm" fontWeight="thin" textColor="secondary">
                {addressInfo.addressText3}
              </Text>
            )}
            {!!addressInfo.addressText4 && (
              <Text fontSize="sm" fontWeight="thin" textColor="secondary">
                {addressInfo.addressText4}
              </Text>
            )}
            {!!addressInfo.addressText5 && (
              <Text fontSize="sm" fontWeight="thin" textColor="secondary">
                {addressInfo.addressText5}
              </Text>
            )}
          </Flex>
        </Flex>
        {addressInfo.edit && (
          <Flex align="center" justify="center">
            <RedoButton
              dataTestId="address-edit-button"
              hierarchy={continueDisabled ? "primary" : "secondary"}
              onClick={addressInfo.edit}
              size="md"
              text="Edit"
            />
          </Flex>
        )}
      </Flex>

      {hasAddressError && (
        <Flex dir="column" gap="md" mt="md">
          {blockingError && (
            <AddressError errorMessage={blockingError.message} />
          )}
          {showAddressVerificationError && (
            <AddressError errorMessage="Address cannot be verified." />
          )}
        </Flex>
      )}
    </Flex>
  );
}

export const ShippingLineOfText = memo(function ShippingLineOfText({
  lineOfText,
}: {
  lineOfText: string;
}) {
  return (
    <Text fontSize="sm" fontWeight="thin" textColor="secondary">
      {lineOfText}
    </Text>
  );
});

function AddressError({ errorMessage }: { errorMessage: string }) {
  return (
    <Flex
      align="center"
      borderColor="error"
      borderStyle="solid"
      borderWidth="1px"
      className="w-full"
      gap="lg"
      p="sm"
      radius="md"
    >
      <RedoFeaturedIcon color="error" Icon={AlertCircleIcon} size="sm" />
      <Text fontSize="sm" textColor="error">
        {errorMessage}
      </Text>
    </Flex>
  );
}

export const AddressOld = memo(function Address({
  address,
  title,
  edit,
}: {
  address?: RedoAddress;
  title: string;
  edit?: () => void;
}) {
  let secondLine = "";
  if (address) {
    secondLine = filterTruthy([address.city, address.state]).join(", ");
    if (address.zip) {
      secondLine += ` ${address.zip}`;
    }
  }
  return (
    <Flex
      dir="column"
      gap="md"
      style={{ borderColor: "#E5E5E5", borderWidth: 1, borderRadius: 12 }}
    >
      <Text fontSize="sm" fontWeight="semibold" textColor="primary">
        {title}
      </Text>
      {address && (
        <Flex dir="column" gap="none" pt="sm">
          <ShippingLineOfText lineOfText={address.name} />
          <ShippingLineOfText lineOfText={address.street1} />
          <ShippingLineOfText lineOfText={address.street2 || ""} />
          <ShippingLineOfText lineOfText={secondLine} />
          <ShippingLineOfText lineOfText={address.country} />
        </Flex>
      )}
      {!address && (
        <ShippingLineOfText lineOfText="Same as returning items from." />
      )}
      {edit && (
        <RedoButton hierarchy="tertiary" onClick={() => edit()} text="Edit" />
      )}
    </Flex>
  );
});
